import { hexToRgb } from './color'
import { gridToCm } from './beadData'

const MIN_CELL_FOR_CODE = 16
const BRAND_LOGO_URL = '/brand-export.png?v=transparent-1'

let brandLogoPromise = null
function loadBrandLogo() {
  if (!brandLogoPromise) {
    brandLogoPromise = new Promise((resolve) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => resolve(null)
      image.src = BRAND_LOGO_URL
    })
  }
  return brandLogoPromise
}

// 动态导入 jsPDF，避免在应用启动时加载（减少初始包体积，避免潜在的初始化问题）
let jsPDFModule = null
async function getJsPDF() {
  if (!jsPDFModule) {
    jsPDFModule = await import('jspdf')
  }
  return jsPDFModule.jsPDF
}

function getDisplayName(user) {
  return user?.username || user?.email || ''
}

/**
 * 触发浏览器下载 dataURL 文件。
 * 兼容夸克/UC 等移动端浏览器：它们对大体积 data: URL 的 a[download] 支持差，
 * 改用 Blob URL + 挂载 DOM 后点击；失败则新窗口打开，供用户长按保存。
 */
export async function downloadDataUrl(dataUrl, filename) {
  try {
    const res = await fetch(dataUrl)
    const blob = await res.blob()
    downloadBlob(blob, filename)
  } catch (err) {
    console.error('[download] blob download failed, fallback to new tab:', err)
    window.open(dataUrl, '_blank')
  }
}

/** Blob 直接下载（PDF 用），失败时新窗口打开 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  try {
    const link = document.createElement('a')
    link.download = filename
    link.href = url
    document.body.appendChild(link)
    link.click()
    link.remove()
  } catch (err) {
    console.error('[download] blob link failed, fallback to new tab:', err)
    window.open(url, '_blank')
  }
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}

function isWhiteColor(color) {
  return color?.hex?.toLowerCase() === '#ffffff'
}

function isPremiumUser(user) {
  if (!user) return false
  return Boolean(user.isPremium && Number(user.premiumUntil) > Date.now())
}

export function createPatternWatermarkCanvas(width, height, text, variant = 'brand') {
  const canvas = document.createElement('canvas')
  canvas.width = width * 2
  canvas.height = height * 2
  const ctx = canvas.getContext('2d')
  ctx.scale(2, 2)
  ctx.save()
  ctx.translate(width / 2, height / 2)
  ctx.rotate(-Math.PI / 7)

  const isBrand = variant === 'brand'
  const fontSize = Math.max(12, Math.min(isBrand ? 32 : 38, Math.min(width, height) * 0.055))
  ctx.font = `600 ${fontSize}px 'PingFang SC', 'Microsoft YaHei', sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.lineJoin = 'round'

  const label = String(text || '').trim()
  if (!label) {
    ctx.restore()
    return canvas
  }

  const stepX = Math.max(fontSize * 5.6, ctx.measureText(label).width + fontSize * 3.2)
  const stepY = fontSize * 4.2
  const range = Math.hypot(width, height)
  let row = 0
  for (let y = -range; y <= range; y += stepY) {
    const offset = row % 2 ? stepX / 2 : 0
    for (let x = -range; x <= range; x += stepX) {
      ctx.strokeStyle = isBrand ? 'rgba(78, 65, 91, 0.15)' : 'rgba(255, 255, 255, 0.24)'
      ctx.lineWidth = Math.max(1, fontSize * 0.07)
      ctx.strokeText(label, x + offset, y)
      ctx.fillStyle = isBrand ? 'rgba(255, 255, 255, 0.32)' : 'rgba(47, 40, 54, 0.16)'
      ctx.fillText(label, x + offset, y)
    }
    row += 1
  }
  ctx.restore()
  return canvas
}

/**
 * 排除与图纸边界相连的白色（背景/边框），只统计主体区域用量。
 * 主体内部的白色孤岛会被保留。
 */
function getSubjectStats(pattern) {
  const { grid, legend, total: rawTotal } = pattern
  if (!grid || !grid.length) {
    const counts = new Map()
    for (const item of legend || []) counts.set(item.id, item.count)
    return { counts, total: rawTotal || 0 }
  }

  const h = grid.length
  const w = grid[0]?.length || 0
  const whiteIds = new Set((legend || []).filter(isWhiteColor).map((c) => c.id))

  if (whiteIds.size === 0 || h === 0 || w === 0) {
    const counts = new Map()
    for (const item of legend || []) counts.set(item.id, item.count)
    return { counts, total: rawTotal || 0 }
  }

  // visited 同时标记“需要排除的背景/边框白色”
  const visited = Array.from({ length: h }, () => Array(w).fill(false))
  const stack = []

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (y === 0 || y === h - 1 || x === 0 || x === w - 1) {
        if (whiteIds.has(grid[y][x]) && !visited[y][x]) {
          stack.push([x, y])
          visited[y][x] = true
        }
      }
    }
  }

  const dirs = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ]
  while (stack.length) {
    const [cx, cy] = stack.pop()
    for (const [dx, dy] of dirs) {
      const nx = cx + dx
      const ny = cy + dy
      if (nx >= 0 && nx < w && ny >= 0 && ny < h && !visited[ny][nx] && whiteIds.has(grid[ny][nx])) {
        visited[ny][nx] = true
        stack.push([nx, ny])
      }
    }
  }

  const counts = new Map()
  let total = 0
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (visited[y][x]) continue
      const id = grid[y][x]
      if (!id) continue
      counts.set(id, (counts.get(id) || 0) + 1)
      total++
    }
  }

  return { counts, total }
}

// 水平镜像：返回新 pattern，不修改原对象（legend / total 不变）
function mirrorPatternHorizontally(pattern) {
  return {
    ...pattern,
    grid: pattern.grid.map((row) => [...row].reverse()),
  }
}

function buildExportLegend(pattern) {
  const { counts } = getSubjectStats(pattern)
  return (pattern.legend || [])
    .map((item) => ({ ...item, count: counts.has(item.id) ? counts.get(item.id) : item.count }))
    .filter((item) => item.count > 0)
    // 按色号字母+数字自然序排列（A1, A2, B1...），方便实体店挑豆
    .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }))
}

function drawHeader(ctx, width, height, user, authorName, brandLogo) {
  ctx.fillStyle = '#FFF5E6'
  ctx.fillRect(0, 0, width, height)
  ctx.fillStyle = '#5D4E6D'
  // 左上角署名：© 原作者（不暴露用户 ID），字号加大
  ctx.font = 'bold 22px sans-serif'
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'left'
  ctx.fillText(`© ${authorName || getDisplayName(user)}`, 24, height / 2)
  if (brandLogo) {
    const maxLogoWidth = Math.min(240, width * 0.44)
    const maxLogoHeight = height - 20
    const scale = Math.min(maxLogoWidth / brandLogo.naturalWidth, maxLogoHeight / brandLogo.naturalHeight)
    const logoWidth = brandLogo.naturalWidth * scale
    const logoHeight = brandLogo.naturalHeight * scale
    ctx.drawImage(brandLogo, width - 24 - logoWidth, (height - logoHeight) / 2, logoWidth, logoHeight)
  } else {
    ctx.textAlign = 'right'
    ctx.font = 'bold 34px sans-serif'
    ctx.fillText('豆田造物', width - 24, height / 2 - 10)
    ctx.font = '16px sans-serif'
    ctx.fillStyle = '#8B7E9A'
    ctx.fillText('深耕豆田，粒造万物', width - 24, height / 2 + 20)
  }
}

const AXIS_LABEL_COLOR = '#495057' // 坐标数字深灰
const AXIS_STRIP_BG = '#F1EFF4' // 坐标条浅灰底

function renderPatternToCanvas(pattern, brand, cellSize, showGrid, showGroupLines, showColorCodes, axisLabels = false) {
  const { grid, width, height } = pattern
  const strip = axisLabels ? 1 : 0 // 四边坐标条（格数）
  const canvas = document.createElement('canvas')
  canvas.width = (width + strip * 2) * cellSize
  canvas.height = (height + strip * 2) * cellSize
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingEnabled = false

  const colorMap = new Map(brand.colors.map((c) => [c.id, c.hex]))
  const nameMap = new Map(brand.colors.map((c) => [c.id, c.name]))

  // 坐标条底色 + 与图纸的分隔线（仅导出图纸显示）
  if (axisLabels) {
    const totalW = canvas.width
    const totalH = canvas.height
    ctx.fillStyle = AXIS_STRIP_BG
    ctx.fillRect(0, 0, totalW, cellSize) // 上
    ctx.fillRect(0, totalH - cellSize, totalW, cellSize) // 下
    ctx.fillRect(0, 0, cellSize, totalH) // 左
    ctx.fillRect(totalW - cellSize, 0, cellSize, totalH) // 右
    ctx.strokeStyle = 'rgba(60, 50, 70, 0.45)'
    ctx.lineWidth = Math.max(1, Math.floor(cellSize / 16))
    ctx.strokeRect(cellSize + 0.5, cellSize + 0.5, totalW - cellSize * 2 - 1, totalH - cellSize * 2 - 1)
  }

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const hex = colorMap.get(grid[y][x])
      if (!hex) continue
      ctx.fillStyle = hex
      ctx.fillRect((x + strip) * cellSize, (y + strip) * cellSize, cellSize, cellSize)
    }
  }

  // 坐标数字：X 轴（列号，上/下两边）+ Y 轴（行号，左/右两边），深灰色
  if (axisLabels) {
    const digits = Math.max(String(width).length, String(height).length)
    const fontSize = Math.max(7, Math.floor(cellSize * (digits >= 3 ? 0.3 : 0.38)))
    ctx.fillStyle = AXIS_LABEL_COLOR
    ctx.font = `bold ${fontSize}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    for (let x = 0; x < width; x++) {
      const label = String(x + 1)
      const cx = (x + 1) * cellSize + cellSize / 2
      ctx.fillText(label, cx, cellSize / 2) // 上
      ctx.fillText(label, cx, canvas.height - cellSize / 2) // 下
    }
    for (let y = 0; y < height; y++) {
      const label = String(y + 1)
      const cy = (y + 1) * cellSize + cellSize / 2
      ctx.fillText(label, cellSize / 2, cy) // 左
      ctx.fillText(label, canvas.width - cellSize / 2, cy) // 右
    }
  }

  if (showGrid && cellSize >= 6) {
    const thinWidth = Math.max(1, Math.floor(cellSize / 16))
    // 细网格
    ctx.strokeStyle = 'rgba(60, 50, 70, 0.25)'
    ctx.lineWidth = thinWidth
    ctx.beginPath()
    for (let i = 0; i <= width; i++) {
      const x = (i + strip) * cellSize + 0.5
      ctx.moveTo(x, strip * cellSize)
      ctx.lineTo(x, canvas.height - strip * cellSize)
    }
    for (let i = 0; i <= height; i++) {
      const y = (i + strip) * cellSize + 0.5
      ctx.moveTo(strip * cellSize, y)
      ctx.lineTo(canvas.width - strip * cellSize, y)
    }
    ctx.stroke()
  }

  // 每 5×5 一组粗线（拼豆板辅助线）
  if (showGroupLines && cellSize >= 6) {
    const group = 5
    const thinWidth = Math.max(1, Math.floor(cellSize / 16))
    ctx.strokeStyle = 'rgba(60, 50, 70, 0.55)'
    ctx.lineWidth = Math.max(2, thinWidth + 1)
    ctx.beginPath()
    for (let i = 0; i <= width; i += group) {
      const x = (i + strip) * cellSize + 0.5
      ctx.moveTo(x, strip * cellSize)
      ctx.lineTo(x, canvas.height - strip * cellSize)
    }
    for (let i = 0; i <= height; i += group) {
      const y = (i + strip) * cellSize + 0.5
      ctx.moveTo(strip * cellSize, y)
      ctx.lineTo(canvas.width - strip * cellSize, y)
    }
    ctx.stroke()
  }

  if (showColorCodes && cellSize >= MIN_CELL_FOR_CODE) {
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    // 瘦体加粗，缩小字号避免贴近格子边框
    ctx.font = `bold ${Math.max(6, Math.floor(cellSize * 0.24))}px 'Arial Narrow', 'PingFang SC', sans-serif`
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const id = grid[y][x]
        const hex = colorMap.get(id)
        const code = nameMap.get(id) || ''
        if (!hex || !code) continue
        const rgb = hexToRgb(hex)
        const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
        ctx.fillStyle = luminance > 0.5 ? '#1a1a1a' : '#ffffff'
        ctx.fillText(code, (x + strip) * cellSize + cellSize / 2, (y + strip) * cellSize + cellSize / 2)
      }
    }
  }

  return canvas
}

// 渲染导出布局画布（PNG / PDF 共用，保证两种导出排版一致）
function renderExportCanvas({ pattern, brand, user, showGrid = true, showGroupLines = true, showColorCodes = false, beadSize = 5, hd = false, watermarkText = null, authorName = null, authorId = null, brandLogo = null }) {
  // 会员 48px/粒超清；免费用户 16px/粒低清（服务端校验 hd 权益）
  // 画布超 7800px 时收敛，避免超出浏览器 canvas 限制
  const baseCell = hd ? 48 : 16
  const cellSize = Math.max(16, Math.min(baseCell, Math.floor(7800 / pattern.width)))
  // 界面元素（页眉/留白/图例）随豆子尺寸等比浮动，避免图大但文字信息太小
  // 逻辑单位以 16px/粒为基准，实际输出 = 逻辑值 × ui
  const ui = cellSize / 16

  const legend = buildExportLegend(pattern)
  // 以下尺寸均为逻辑单位（16px/粒基准）
  const padding = 24
  const headerHeight = 80
  const legendTitleH = 30
  const legendItemH = 42
  const legendColWidth = 170
  const patternW = (pattern.width + 2) * 16
  const patternH = (pattern.height + 2) * 16
  // 导出图纸带四边坐标轴标注（编辑器内预览不显示）
  const patternCanvas = renderPatternToCanvas(pattern, brand, cellSize, showGrid, showGroupLines, showColorCodes, true)

  const minWidth = patternW + padding * 2
  const legendCols = Math.min(10, Math.max(3, Math.floor(minWidth / legendColWidth)))
  const effectiveCols = Math.max(1, Math.min(legend.length, legendCols))
  const legendRows = Math.ceil(legend.length / effectiveCols)
  const legendHeight = legendTitleH + legendRows * legendItemH + padding
  const width = Math.max(minWidth, effectiveCols * legendColWidth + padding * 2)
  const height = headerHeight + patternH + padding + legendHeight + padding

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * ui)
  canvas.height = Math.round(height * ui)
  const ctx = canvas.getContext('2d')
  // 统一缩放：后续全部按逻辑单位绘制
  ctx.scale(ui, ui)

  ctx.fillStyle = '#FFFBF5'
  ctx.fillRect(0, 0, width, height)

  drawHeader(ctx, width, headerHeight, user, authorName, brandLogo)

  const patternX = Math.max(padding, Math.floor((width - patternW) / 2))
  // patternCanvas 实际分辨率为 cellSize/粒，与 16*ui 一致，1:1 绘制不损失清晰度
  ctx.drawImage(patternCanvas, patternX, headerHeight + padding, patternW, patternH)

  // 非会员 PNG 水印（与 PDF 行为一致，配合服务端 hd 校验形成付费墙）
  if (!isPremiumUser(user)) {
    const wmCanvas = createPatternWatermarkCanvas(patternW, patternH, '豆田造物', 'brand')
    ctx.drawImage(wmCanvas, patternX, headerHeight + padding, patternW, patternH)
  }

  // 会员自定义水印：斜向平铺，仅覆盖图纸，不进入页眉和图例。
  if (watermarkText && isPremiumUser(user)) {
    const wmCanvas = createPatternWatermarkCanvas(patternW, patternH, watermarkText, 'custom')
    ctx.drawImage(wmCanvas, patternX, headerHeight + padding, patternW, patternH)
  }

  let ly = headerHeight + patternH + padding * 2
  ctx.fillStyle = '#5D4E6D'
  ctx.font = 'bold 18px sans-serif'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  const { total: subjectTotal } = getSubjectStats(pattern)
  // 标题过长时自动缩小字号，避免小画板导出时右侧被裁掉
  const titleText = `色号图例 & 用量 · ${brand.name} · 规格 ${pattern.width}×${pattern.height} · 共 ${legend.length} 色 · 共 ${subjectTotal} 豆 · 约 ${gridToCm(pattern.width, beadSize)} × ${gridToCm(pattern.height, beadSize)} cm`
  const maxTitleW = width - padding * 2
  let titleFontSize = 18
  ctx.font = `bold ${titleFontSize}px sans-serif`
  while (ctx.measureText(titleText).width > maxTitleW && titleFontSize > 10) {
    titleFontSize -= 1
    ctx.font = `bold ${titleFontSize}px sans-serif`
  }
  ctx.fillText(titleText, padding, ly)
  ly += legendTitleH

  const colWidth = (width - padding * 2) / effectiveCols
  legend.forEach((item, idx) => {
    const col = idx % effectiveCols
    const row = Math.floor(idx / effectiveCols)
    const x = padding + col * colWidth
    const iy = ly + row * legendItemH
    // 长条色卡，文字写在色卡上（按底色亮度自动选黑/白）
    const chipW = colWidth - 12
    const chipH = 34
    ctx.fillStyle = item.hex
    ctx.fillRect(x, iy, chipW, chipH)
    ctx.strokeStyle = 'rgba(0,0,0,0.12)'
    ctx.strokeRect(x, iy, chipW, chipH)
    const rgb = hexToRgb(item.hex)
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
    ctx.fillStyle = luminance > 0.55 ? '#1a1a1a' : '#ffffff'
    ctx.font = 'bold 14px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(`${item.name} ×${item.count}`, x + chipW / 2, iy + chipH / 2)
  })
  ctx.textAlign = 'left'

  return canvas
}

export async function exportPatternPng({ mirrored = false, ...opts }) {
  // 镜像在布局渲染前处理
  if (mirrored) opts.pattern = mirrorPatternHorizontally(opts.pattern)
  const brandLogo = await loadBrandLogo()
  return renderExportCanvas({ ...opts, brandLogo }).toDataURL('image/png')
}

export async function exportPatternPdf({ pattern, brand, user, showGrid = true, showGroupLines = true, showColorCodes = false, maxWidth = 800, beadSize = 5, watermarkText = null, mirrored = false, authorName = null, authorId = null, hd = false }) {
  if (mirrored) pattern = mirrorPatternHorizontally(pattern)
  const [jsPDF, brandLogo] = await Promise.all([getJsPDF(), loadBrandLogo()])

  // 与 PNG 导出完全一致的排版：渲染同一布局画布，整页嵌入 PDF
  const canvas = renderExportCanvas({
    pattern,
    brand,
    user,
    showGrid,
    showGroupLines,
    showColorCodes,
    beadSize,
    hd,
    watermarkText,
    authorName,
    authorId,
    brandLogo,
  })

  // JPEG 压缩嵌入，控制文件体积（< 30MB）；像素图纸为高分辨率位图，视觉无损
  const dataUrl = canvas.toDataURL('image/jpeg', 0.92)

  // 页面尺寸按图纸实际比例自适应（宽度 maxWidth，高度按比例）
  const pageW = maxWidth
  const pageH = Math.round(maxWidth * (canvas.height / canvas.width))
  const pdf = new jsPDF({
    orientation: canvas.width > canvas.height ? 'l' : 'p',
    unit: 'px',
    format: [pageW, pageH],
  })
  pdf.addImage(dataUrl, 'JPEG', 0, 0, pageW, pageH)
  return pdf
}
